/**
 * Navigation model for the Hubs shell (left rail, fly-outs, mobile drawer, Hub
 * landing pages, global search). Pure functions over hubs.ts, so the rail, the
 * drawer and the Hub page all show the same sections in the same order.
 *
 * Layout decision: Jon, 2026-11-03 (07-hubs §6) — a HubSpot-style left rail with
 * cascading fly-outs replaced the card grids inside the Hubs. The entryway keeps
 * its tiles.
 */
import type { User } from '../types';
import { canSeeItem, findHub, findItem, itemsForHub, visibleHubs } from './hubs';
import type { Hub, HubId, HubItem, HubListing } from './hubs';

// ─── Rail ────────────────────────────────────────────────────────────────────

export interface RailGroups {
  /** Home (shown after "All Hubs" and before "Pinned"). */
  top: Hub[];
  /** Shared and department Hubs, entryway order. */
  main: Hub[];
  /** Admin, last, after a divider. */
  bottom: Hub[];
}

/** The rail's Hubs for a user, in entryway order (client-role users get none). */
export function railGroups(user: User | null | undefined): RailGroups {
  const hubs = visibleHubs(user);
  return {
    top: hubs.filter((h) => h.id === 'home'),
    main: hubs.filter((h) => h.id !== 'home' && h.id !== 'admin'),
    bottom: hubs.filter((h) => h.id === 'admin'),
  };
}

/** True when a Hub has items, so its rail row opens a fly-out instead of navigating. */
export function hubHasFlyout(hubId: HubId, user: User | null | undefined): boolean {
  return itemsForHub(hubId, user).length > 0;
}

// ─── Sections (fly-out + Hub page) ───────────────────────────────────────────

export interface NavSection {
  id: 'own' | 'also';
  label: string;
  listings: HubListing[];
}

/** A Hub's items split into its own section, then "Also in <Hub>". Empty sections dropped. */
export function hubSections(hub: Hub, user: User | null | undefined): NavSection[] {
  const listings = itemsForHub(hub.id, user);
  const sections: NavSection[] = [
    { id: 'own', label: hub.title, listings: listings.filter((l) => l.isHome) },
    { id: 'also', label: `Also in ${hub.title}`, listings: listings.filter((l) => !l.isHome) },
  ];
  return sections.filter((s) => s.listings.length > 0);
}

export function isAdminOnly(item: HubItem): boolean {
  return Boolean(item.roles && item.roles.length === 1 && item.roles[0] === 'dwa_admin');
}

export function isComingSoon(item: HubItem): boolean {
  return item.status === 'coming-soon' || !item.route;
}

/**
 * Tooltip notes for an item as listed in `hubId`: "Admin only", "Coming soon",
 * "Lives in <home Hub>" (for "also in" listings).
 */
export function itemNotes(item: HubItem, hubId?: HubId): string[] {
  const notes: string[] = [];
  if (isComingSoon(item)) notes.push('Coming soon');
  if (isAdminOnly(item)) notes.push('Admin only');
  if (hubId && item.home !== hubId) {
    const home = findHub(item.home);
    if (home) notes.push(`Lives in ${home.title}`);
  }
  return notes;
}

// ─── Pinned ──────────────────────────────────────────────────────────────────

/** Pinned item ids → items the user can still open (unknown or hidden pins dropped). */
export function pinnedItems(pins: string[], user: User | null | undefined): HubItem[] {
  return pins
    .map((id) => findItem(id))
    .filter((i): i is HubItem => Boolean(i && i.route && canSeeItem(user, i)));
}

// ─── Global search ───────────────────────────────────────────────────────────

export type SearchResult =
  | { kind: 'hub'; id: string; title: string; subtitle: string; route: string; hub: Hub }
  | { kind: 'item'; id: string; title: string; subtitle: string; route?: string; item: HubItem; comingSoon: boolean }
  | { kind: 'kb'; id: 'kb-search'; title: string; subtitle: string; route: string };

function score(query: string, title: string, description = ''): number {
  const t = title.toLowerCase();
  const d = description.toLowerCase();
  if (t === query) return 4;
  if (t.startsWith(query)) return 3;
  if (t.split(/\s+/).some((w) => w.startsWith(query))) return 2.5;
  if (t.includes(query)) return 2;
  if (d.includes(query)) return 1;
  return 0;
}

/**
 * Hubs and items the user can see whose title or description match `raw`, best
 * first, then "Search Knowledgebase for …" when the user has the Knowledgebase Hub.
 */
export function searchHubs(raw: string, user: User | null | undefined, limit = 8): SearchResult[] {
  const query = raw.trim().toLowerCase();
  if (!query) return [];
  const hubs = visibleHubs(user);
  const scored: { s: number; r: SearchResult }[] = [];

  for (const hub of hubs) {
    const s = score(query, hub.title, hub.description);
    if (s > 0) {
      scored.push({ s: s + 0.5, r: { kind: 'hub', id: `hub-${hub.id}`, title: hub.title, subtitle: 'Hub', route: hub.route, hub } });
    }
  }

  const seen = new Set<string>();
  for (const hub of hubs) {
    for (const { item } of itemsForHub(hub.id, user)) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      const s = score(query, item.title, item.description);
      if (s > 0) {
        const home = findHub(item.home);
        const comingSoon = isComingSoon(item);
        scored.push({
          s: comingSoon ? s - 0.25 : s,
          r: {
            kind: 'item',
            id: `item-${item.id}`,
            title: item.title,
            subtitle: home?.title ?? '',
            route: comingSoon ? undefined : item.route,
            item,
            comingSoon,
          },
        });
      }
    }
  }

  const results = scored
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.r);

  if (hubs.some((h) => h.id === 'knowledgebase')) {
    results.push({
      kind: 'kb',
      id: 'kb-search',
      title: `Search Knowledgebase for “${raw.trim()}”`,
      subtitle: 'SOPs and documents',
      route: `/hubs/knowledgebase?q=${encodeURIComponent(raw.trim())}`,
    });
  }
  return results;
}

// ─── Rail collapse (per browser) ─────────────────────────────────────────────

export const RAIL_COLLAPSED_KEY = 'ow.hubs.railCollapsed';

export function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeRailCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* storage unavailable: the rail just won't remember */
  }
}

// ─── Lupa widget ─────────────────────────────────────────────────────────────

/** Window event the top bar's "Ask Lupa" button fires; the global widget opens on it. */
export const OPEN_LUPA_EVENT = 'ow:open-lupa';

/**
 * Open the Lupa widget. With `prompt`, the message box is filled in (not sent) so
 * the person can edit it first: Home's "Lupa can help" tips use this.
 */
export function openLupaWidget(prompt?: string): void {
  window.dispatchEvent(new CustomEvent<{ prompt?: string }>(OPEN_LUPA_EVENT, { detail: { prompt } }));
}
